import React from "react";
import type { GetServerSidePropsContext } from "next";
import nr from "newrelic";
import { QuickAddProvider } from "@foundation/quickadd-provider";
import { parseGeoInfo } from "@foundation/sparkle/lib/adDials/parseGeoInfo";
import SectionFrontLayout from "src/~components/SectionFrontLayout";
import { PageWrapper, PageWrapperProps } from "src/~components/PageWrapper";
import { type SectionFrontPage } from "~types/types";
import { PortfolioPageProvider } from "~context/PortfolioPageContext";
import { createSectionFrontServerSideProps } from "src/~data/section-front";
import { parsePath } from "~utils/pathUtils";
import { getAdTargeting } from "~utils/AdTargetingClient";
import { getCurrentUrl } from "~utils/nextContextUtil";
import { getAdCode } from "~utils/adUtils";
import { getAdConfig } from "~api/getAdConfig";
import { getLanguageCodeLocale, getValidLocale } from "~constants/i18n";

export type PageProps = PageWrapperProps & {
  sectionFront: SectionFrontPage;
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { res, locale } = context;
  const { slug } = context.query;

  // Convert the slug array to a string path
  const path = Array.isArray(slug) ? `/${slug.join("/")}` : "/";

  // Parse the path in to section front segments
  const pathSegments = parsePath(path);

  // remove markets from path
  // TODO: fix envoy to not send markets
  if (pathSegments[0] === "markets") {
    pathSegments.shift();
  }

  const sectionFrontName = pathSegments.shift() as string;
  const pageType = `${sectionFrontName}-bazaar`;

  // get language code from locale
  const languageCodeLocale = getLanguageCodeLocale(locale);
  const validLocale = getValidLocale(languageCodeLocale);

  const sectionFrontServerSideProps =
    createSectionFrontServerSideProps(validLocale);

  const pageProps: PageProps =
    await sectionFrontServerSideProps.load<PageProps>({
      pageType,
      sectionFrontName,
      sectionFrontPath: pathSegments,
      locale: validLocale,
      context: ["bazaar", "media-ui-core", "media-ui-nav", "media-ui-markets"],
    });

  if (!pageProps.sectionFront) {
    nr.addCustomAttributes({
      sectionFrontStatus: "Not Found",
      invalidSectionFront: true,
      sectionFrontName,
    });

    return {
      notFound: true,
    };
  }

  if (pageProps.partial) {
    res.statusCode = 304;
    res.setHeader("Cache-Control", "no-cache");

    nr.addCustomAttributes({
      partialDataLoad: true,
    });

    return { props: { pageProps: { partial: true } as PageProps } };
  }

  const analyticsAdCode =
    pageProps.sectionFront.sectionFrontTab.analytics?.adcode;
  const adCode = getAdCode(pageType, analyticsAdCode, validLocale);
  const adTargeting = await getAdTargeting({
    pageId: pageType,
    adPageType: "other",
    adCode,
    url: getCurrentUrl(context),
  });
  const adConfig = getAdConfig({
    ...parseGeoInfo(context.req.cookies?.geo_info),
    adCode,
  });

  return {
    props: {
      ...pageProps,
      removeTickerBar: true,
      pageId: "section-front-bazaar",
      pageType,
      countryCode: context.req.cookies?.country_code || "US",
      adTargeting,
      adConfig,
    },
  };
};

export default function SectionFrontPage(props: PageProps) {
  const { sectionFront } = props;

  // no paywall on currencies
  const fortressOptions = {};

  const pageDataProps = {
    ...props,
    fortressOptions,
    analytics: sectionFront.sectionFrontTab.analytics,
    metadata: sectionFront.sectionFrontTab.metadata,
    disablePaywall: true,
  };

  const WATCHLIST_SERVICE_URL = `${
    process.env.NEXT_PUBLIC_REGISTRATION_URL || "https://login.bloomberg.com"
  }/watchlist-service`;

  return (
    <PageWrapper {...pageDataProps}>
      <PortfolioPageProvider>
        <QuickAddProvider watchlistServiceUrl={WATCHLIST_SERVICE_URL}>
          <SectionFrontLayout
            sectionTitle="Markets"
            sectionFrontPage={sectionFront}
          />
        </QuickAddProvider>
      </PortfolioPageProvider>
    </PageWrapper>
  );
}
